"use client";

import { FC, useEffect, useRef, useState } from "react";
import { BuilderBlockProps } from "@/types";

export type IframeEmbedCodeBlockProps = {
  iframeContentType?: "page" | "elements" | "demo";
  iframeTitle?: string;
  iframeHeight?: number;
  iframeWidth?: number;
  iframeInitialHeight?: number;
  iframeContent?: string;
  iframeSrc?: string;
  iframeLoading?: "lazy" | "eager";
  allowFullScreen?: boolean;
  trackAnalyticsInsideIframe?: boolean;
  // used for navattic call in conditional scripts
  campaignId?: string;
  containerId?: string;
} & BuilderBlockProps;

const MIN_HEIGHT = 600;
const DEFAULT_CONTAINER_ID = "inner-iframe-container";

const getBodyYMargins = (body: HTMLElement) => {
  const iframeBodyStyle = window.getComputedStyle(body);

  return (
    (Number.parseFloat(iframeBodyStyle.getPropertyValue("margin-top")) || 0) +
    (Number.parseFloat(iframeBodyStyle.getPropertyValue("margin-bottom")) || 0)
  );
};

export const IframeEmbedCodeBlock: FC<IframeEmbedCodeBlockProps> = ({
  iframeContentType,
  iframeTitle = "Embed content",
  iframeHeight,
  iframeWidth,
  iframeInitialHeight = MIN_HEIGHT,
  iframeContent = "",
  iframeSrc,
  iframeLoading = "eager",
  campaignId,
  containerId = DEFAULT_CONTAINER_ID,
  allowFullScreen,
  trackAnalyticsInsideIframe,
  attributes,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number>(iframeInitialHeight);
  const [blobUrl, setBlobUrl] = useState<string>("");
  const style =
    iframeContentType === "elements"
      ? { height: `${height}px`, width: "100%" }
      : {};

  // Set up blob url for HTML content embeds
  useEffect(() => {
    if (!iframeContent || iframeSrc) {
      return;
    }

    const html =
      iframeContentType === "page"
        ? iframeContent
        : iframeContentType === "elements"
          ? `<div id="${containerId}">${iframeContent}</div>`
          : "";
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);

    // eslint-disable-next-line
    setBlobUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [containerId, iframeContent, iframeSrc, iframeContentType]);

  // Set up resize observer when iframe loads
  useEffect(() => {
    if (!blobUrl || iframeSrc || iframeContentType !== "elements") return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    let resizeObserver: ResizeObserver | null = null;

    const handleLoad = () => {
      const iframeBody = iframe.contentWindow?.document.body;

      if (iframeBody) {
        const innerIframeContainer =
          iframe.contentWindow?.document.getElementById(containerId);

        if (innerIframeContainer) {
          resizeObserver?.disconnect();
          resizeObserver = new ResizeObserver(() => {
            setHeight(
              Math.max(
                iframeInitialHeight,
                innerIframeContainer.scrollHeight + getBodyYMargins(iframeBody),
              ),
            );
          });

          resizeObserver.observe(innerIframeContainer);
        } else {
          resizeObserver?.disconnect();
          resizeObserver = new ResizeObserver(() => {
            setHeight(
              Math.max(
                iframeInitialHeight,
                iframeBody.scrollHeight + getBodyYMargins(iframeBody),
              ),
            );
          });

          resizeObserver.observe(iframeBody);
        }
      }
    };

    iframe.addEventListener("load", handleLoad);

    // If already loaded, run immediately
    if (iframe.contentWindow?.document.readyState === "complete") {
      handleLoad();
    }

    return () => {
      iframe.removeEventListener("load", handleLoad);
      resizeObserver?.disconnect();
      resizeObserver = null;
    };
  }, [blobUrl, containerId, iframeContentType, iframeInitialHeight, iframeSrc]);

  // Set up click tracking for analytics inside iframe
  useEffect(() => {
    if (!trackAnalyticsInsideIframe) return;

    const frame = iframeRef.current;
    if (!frame) return;

    let attachedDoc: Document | null = null;

    function clickHandler(e: Event) {
      const el = (e.target as Element | null)?.closest?.(
        "[data-linktype][data-linkdetail]",
      );

      if (!el) return;

      const dlt = el?.getAttribute("data-linktype") || undefined;
      const dld = el?.getAttribute("data-linkdetail") || undefined;

      console.log("iframe click: dlt=" + dlt + ", dld=" + dld);

      window.parent.document.dispatchEvent(
        new CustomEvent("iframe:click", { detail: { dlt, dld } }),
      );
    }

    const attachClickHandler = () => {
      const doc = frame?.contentDocument ?? null;
      if (doc === attachedDoc) return;
      attachedDoc?.removeEventListener("click", clickHandler, true);
      attachedDoc = doc;
      attachedDoc?.addEventListener("click", clickHandler, true);
    };

    frame.addEventListener("load", attachClickHandler);

    if (frame.contentDocument?.readyState === "complete") {
      attachClickHandler();
    }

    return () => {
      frame.removeEventListener("load", attachClickHandler);
      attachedDoc?.removeEventListener("click", clickHandler, true);
      attachedDoc = null;
    };
  }, [blobUrl, iframeSrc, trackAnalyticsInsideIframe]);

  if ((!blobUrl || !iframeContent) && !iframeSrc) return null;

  return (
    <iframe
      {...attributes}
      data-demo-campaign-id={
        iframeContentType === "demo" ? campaignId : undefined
      }
      id={attributes?.id}
      title={iframeTitle}
      height={iframeHeight}
      width={iframeWidth}
      style={{
        ...attributes?.style,
        ...style,
      }}
      ref={iframeRef}
      src={iframeSrc || blobUrl}
      loading={iframeLoading}
      allowFullScreen={allowFullScreen}
      sandbox="allow-scripts allow-forms allow-popups allow-top-navigation-by-user-activation allow-same-origin"
    />
  );
};
